import { gsap } from "gsap";
import type { BackgroundEvent, BackgroundAnimationType } from "../types";

declare global {
  interface HTMLElementEventMap {
    bgControl: BackgroundEvent;
  }
}

const BG_TWEEN_DURATION = 0.5;

const animation: Record<
  BackgroundAnimationType,
  (
    action: BackgroundEvent["detail"]["action"],
    step: number,
    background: HTMLElement,
    timeline: gsap.core.Timeline
  ) => void
> = {
  none: (_a, _s, _bg, _t) => {},
  zoom: (action, step, background, timeline) => {
    const endLabel = `zoom-${step}-end`;
    const startLabel = `zoom-${step}-start`;
    if (action === "in") {
      const hasLabel = endLabel in timeline.labels;
      if (hasLabel) {
        timeline.tweenTo(endLabel, { duration: BG_TWEEN_DURATION });
        return;
      }
      const props = {
        scale: "+=0.3",
        duration: 0.25,
        ease: "power2.inOut",
      };
      timeline.addLabel(startLabel).to(background, props).addLabel(endLabel);
    } else {
      timeline.tweenTo(startLabel, { duration: BG_TWEEN_DURATION });
    }
  },
  "zoom-strong": (action, step, background, timeline) => {
    const endLabel = `zoom-strong-${step}-end`;
    const startLabel = `zoom-strong-${step}-start`;
    if (action === "in") {
      const hasLabel = endLabel in timeline.labels;
      if (hasLabel) {
        timeline.tweenTo(endLabel, { duration: BG_TWEEN_DURATION });
        return;
      }
      const props = { scale: "+=0.5", duration: 0.75 };
      timeline.addLabel(startLabel).to(background, props).addLabel(endLabel);
    } else {
      timeline.tweenTo(startLabel, { duration: BG_TWEEN_DURATION });
    }
  },
  morph: (action, step, background, timeline) => {
    const parent = background.parentElement;
    const endLabel = `morph-${step}-end`;
    const startLabel = `morph-${step}-start`;
    if (action === "in") {
      const hasLabel = endLabel in timeline.labels;
      if (hasLabel) {
        timeline.tweenTo(endLabel, { duration: BG_TWEEN_DURATION });
        return;
      }
      const props = {
        scaleX: "0.025",
        scaleY: "10",
        x: "-48vw",
        y: "100vh",
        duration: 0.75,
        ease: "power2.inOut",
      };
      timeline
        .addLabel(startLabel)
        .to(parent, props)
        .to(parent, { opacity: 0, duration: 0.75 }, ">-0.6")
        .addLabel(endLabel);
    } else {
      timeline.tweenTo(startLabel, { duration: BG_TWEEN_DURATION });
    }
  },
  reset: (action, _s, _bg, timeline) => {
    if (action === "out")
      timeline.tweenTo(timeline.totalDuration(), {
        duration: BG_TWEEN_DURATION,
      });
    else timeline.tweenTo("bg-svg-start", { duration: BG_TWEEN_DURATION });
  },
};

export const backgroundSVGAnimation = (
  background: HTMLElement,
  parent: HTMLElement
) => {
  const BG_LISTENER_KEY = "bgListenerAttached";
  const timeline = gsap.timeline();
  const morphTimeline = gsap.timeline();

  timeline.addLabel("bg-svg-start").to(background, {});

  const scrollytelling = parent.closest<HTMLElement>(
    '[data-component="scrollytelling"]'
  );
  if (!scrollytelling) return;

  if (scrollytelling.dataset[BG_LISTENER_KEY] === "true") return;
  scrollytelling.dataset[BG_LISTENER_KEY] = "true";

  scrollytelling.addEventListener("bgControl", (e: BackgroundEvent) => {
    if (!e.detail) return;
    const { eventName, action, step } = e.detail;
    const tl = eventName === "morph" ? morphTimeline : timeline;
    animation[eventName](action, step, background, tl);
  });
};
